import {
  FlowMessengerActionSubflowReferencePayload,
  SubflowMessengerAction,
} from 'lib-shared-types-api';
import { useMemo } from 'react';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { flowNodeColor } from 'fable/flowEditor';
import { TextBreak } from 'fable/flowEditor/components/flowManager/canvas/node/textBreak';
import { Relation } from 'fable/flowEditor/components/flowManager/canvas/types/relation';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
  NodeConnection,
} from 'flow-builder/types/node';

import {
  FlowMessengerExtraData,
  FlowMessengerNodeSubflowReferencePayload,
} from '../../../flow.types';
import { findUniqueExitPathFromSubflow } from '../../shared/subflows/findUniqueExitPathFromSubflow';
import { SubflowReferenceModal } from '../subflowReferenceModal';

import {
  findSubflowNameById,
  scanForDereferencedSubflows,
} from './subflowUtils';

type SubflowReferenceNodeAttributes = GenericNodeAttributes<
  FlowMessengerNodeSubflowReferencePayload,
  FlowMessengerExtraData
>;

type SubflowReferenceNodeProps = GenericNodeProps<
  FlowMessengerNodeSubflowReferencePayload,
  FlowMessengerExtraData
>;

function countReturnNodeReuse(
  returnId: string,
  allActions: SubflowMessengerAction[],
): number {
  return allActions.reduce((acc, action) => {
    if (
      action.nodeType === FlowNodeTypes.SUBFLOW_RETURN &&
      action.payload.returnId === returnId
    ) {
      return acc + 1;
    }

    return acc;
  }, 0);
}

const translatePayload: SubflowReferenceNodeAttributes['translatePayload'] = (
  { payload },
  extraData,
): NodeConnection[] => {
  const subflowData = extraData.subflows?.find(
    ({ id }) => id === payload.subflowId,
  );

  if (!subflowData) {
    return [];
  }

  /*
  The ports on this node are the unique subflow return actions. Each port
  represents a unique `returnId` from the subflow return actions. The
  `nextNodeId` of the subflow return action is the connectionId of the child
  node that the subflow return action points to.
  */
  const returnNodes = findUniqueExitPathFromSubflow(subflowData.actions);

  const ports = returnNodes
    .map((subflowReturnNode): NodeConnection | undefined => {
      const returnNodeReuseCount = countReturnNodeReuse(
        subflowReturnNode.payload.returnId,
        subflowData.actions,
      );

      const labelSuffix =
        returnNodeReuseCount > 1 ? ` (${returnNodeReuseCount} merged)` : '';

      const connection =
        payload.exits[subflowReturnNode.payload.returnId]?.nextNodeId;

      // This function is called as many times as the flowbuilder re-renders. In some transient renders the connection might not be available.
      // It's important to return undefined in such cases and then filter out the undefined values to avoid runtime crashes.
      if (!connection) {
        return undefined;
      }

      return {
        id: subflowReturnNode.id,
        connection,
        label: subflowReturnNode.payload.label + labelSuffix,
        labelId: subflowReturnNode.id,
      };
    })
    .filter((port): port is NodeConnection => Boolean(port));

  return ports;
};

const onUpdatedConnection: SubflowReferenceNodeAttributes['updateConnection'] =
  (
    subflowReferenceNode,
    childNode,
    newConnectionId: string,
  ): Relation<FlowMessengerActionSubflowReferencePayload, string> => {
    const connectionThatChanged = subflowReferenceNode.connections.find(
      ({ labelId }) => {
        return labelId === childNode.id;
      },
    );

    // a child has been severed
    if (!connectionThatChanged) {
      /**
       * SubflowReferenceNode.payload.exits is a map of <subflowReturnNodeId,
       * subflowReferenceNodechildId> When a child is removed, we need to update
       * the corresponding subflowReturnNodeId from the exits map to point to
       * the new child connectionId otherwise the flow will keep pointing to the
       * old child connectionId.
       */
      const exitPayloadToBeUpdated = Object.entries(
        subflowReferenceNode.payload.exits,
      ).find(([_, value]) => value.nextNodeId === childNode.id);

      const [subflowReturnId] = exitPayloadToBeUpdated || [];

      if (!subflowReturnId) {
        return { ...subflowReferenceNode };
      }

      return {
        ...subflowReferenceNode,
        payload: {
          ...subflowReferenceNode.payload,
          exits: {
            ...subflowReferenceNode.payload.exits,
            [subflowReturnId]: { nextNodeId: newConnectionId },
          },
        },
      };
    }

    if (!connectionThatChanged) {
      return { ...subflowReferenceNode };
    }

    return {
      ...subflowReferenceNode,
      payload: {
        ...subflowReferenceNode.payload,
        exits: {
          ...subflowReferenceNode.payload.exits,
          [connectionThatChanged.id]: { nextNodeId: newConnectionId },
        },
      },
    };
  };

const hiddenNodeTypes: FlowNodeTypes[] = [
  FlowNodeTypes.SUBFLOW_RETURN,
  FlowNodeTypes.INIT,
];

const shouldActionNameBeHidden = (action: SubflowMessengerAction): boolean =>
  !hiddenNodeTypes.includes(action.nodeType as FlowNodeTypes);

export function SubflowReferenceNode({
  node,
  context,
}: SubflowReferenceNodeProps): JSX.Element {
  const { subflows = [], nodeTypes } = context.extraData;

  const {
    payload: { subflowId },
  } = node;

  const subflow = useMemo(
    () => subflows?.find(({ id }) => id === subflowId),
    [subflowId, subflows],
  );

  const actionsList = useMemo(
    () => (
      <>
        {subflow?.actions
          .filter(shouldActionNameBeHidden)
          .map(({ id, nodeType }) => {
            const name = nodeTypes[nodeType]?.label || 'Unknown node type';

            return (
              <TextBreak key={id}>
                <Text weight="normal" text={name} />
              </TextBreak>
            );
          })}
      </>
    ),
    [subflow?.actions, nodeTypes],
  );

  return (
    <TextContent>
      <TextBreak>
        <Text weight="semibold" text={subflow?.name ?? 'Unknown subflow'} />
      </TextBreak>

      {actionsList}
    </TextContent>
  );
}

export const SubflowReferenceNodeAttributes: SubflowReferenceNodeAttributes = {
  iconName: 'merge',
  label: 'Subflow',
  color: hexStringToNumber(flowNodeColor.purple),
  translatePayload,
  updateConnection: onUpdatedConnection,
  NodeContent: SubflowReferenceNode,
  ActionModal: SubflowReferenceModal,
  helpText:
    'Sub-flows" are reusable branches that you can insert into various parts of the main flow for repeated use.',
  onBeforeDelete(nodeId, flow) {
    const { dereferencedSubflowsIds, needsCustomConfirmation } =
      scanForDereferencedSubflows({
        flow,
        selectedNodesIds: [nodeId],
      });

    if (needsCustomConfirmation) {
      const subFlowNames = dereferencedSubflowsIds
        .map(findSubflowNameById(flow))
        .join(', ');

      return {
        customConfirmModalArgs: {
          title: `Confirm deletion of ${subFlowNames} sub-flow${
            subFlowNames.length ? 's' : ''
          }?`,
          description: `You are about to delete the last sub-flow node. This action cannot be undone, and the node and its contents will be permanently lost.`,
          confirm: {
            label: 'Delete last node',
          },
        },
      };
    }
  },
};
